import {Injectable} from "@angular/core";
import {BaseApiService} from "../services/base-api-service";
import {Observable, of} from "rxjs";
import {Merchant} from "../interfaces/Merchant";
import {RequestType} from "../enums/RequestType";
import {ProductsApiResponse} from "../interfaces/ProductsApiResponse";
import {ApiType} from "../enums/api-type";
import {RegisterMerchantRequest} from "../interfaces/register-merchant-request";
import {JWTToken} from "../interfaces/JWTToken";
import {map} from "rxjs/operators";
import {MerchantSearchResponse} from "../interfaces/MerchantSearchResponse";
import {MerchantFilters} from "../interfaces/MerchantFiltersResponse";
import {AppliedMerchantFilters} from "../interfaces/AppliedMerchantFilters";
import {ClaimMerchantRequest} from "../interfaces/ClaimMerchantRequest";
import {SocialMedia} from "../interfaces/SocialMedia";
import {MerchantPhone} from "../interfaces/MerchantPhone";
import {RatingApiResponse} from "../interfaces/Rating";
import {toVoid} from "../extensions/rx-extensions";
import { AuthService } from "../../website/services/auth.service";

@Injectable({providedIn: 'root'})
export class MerchantApi {

  constructor(
    private readonly apiClient: BaseApiService,
    private readonly authService: AuthService
  ) {
  }

  search(pageNumber: number, textToSearchFor: string, appliedFilters?: AppliedMerchantFilters): Observable<MerchantSearchResponse> {
    let apiType = ApiType.merchantSearch
    const result: { [key: string]: any } = {};
    if (appliedFilters !== undefined && appliedFilters !== null) {
      if (appliedFilters?.deliveryAvailable) {
        result["filters[deliveryAvailable]"] = true
      }
      if (appliedFilters?.pickupAvailable) {
        result["filters[pickupAvailable]"] = true
      }
      if (appliedFilters?.price.from !== null) {
        result["filters[gteqprice]"] = appliedFilters?.price.from
      }
      if (appliedFilters?.price.to !== null) {
        result["filters[ltprice]"] = appliedFilters?.price.to
      }
      for (let filter of appliedFilters.appliedFilters) {
        if (filter.filters.length > 0) {
          result[`filters[${filter.name}]`] = JSON.stringify(filter.filters)
        }
      }
    }
    if (Object.keys(result).length === 0 && textToSearchFor.length === 0) {
      apiType = ApiType.getMerchants
    }
    result['pageSize'] = 15
    result['page'] = pageNumber
    return this.apiClient.call<any, MerchantSearchResponse>({
      apiType: apiType,
      pathVariables: textToSearchFor.length > 0 ? [`/${textToSearchFor}`] : [''],
      requestType: RequestType.GET,
      body: result
    })
  }

  getMerchants(pageNumber: number): Observable<MerchantSearchResponse> {
    let params = {
      "pageSize": 15,
      "page": pageNumber
    }
    return this.apiClient.call<{}, MerchantSearchResponse>({
      apiType: ApiType.getMerchants,
      requestType: RequestType.GET,
      body: params
    })
  }

  getMerchantById(id: string, withTopProducts: boolean): Observable<Merchant> {
    return this.apiClient.call<{}, Merchant>({
      apiType: ApiType.getMerchantById,
      pathVariables: [id],
      requestType: RequestType.GET,
      body: withTopProducts ? {
        "withTopProducts": withTopProducts
      } : {}
    })
  }

  getProductsByMerchantId(merchantId: string, pageNumber: number): Observable<ProductsApiResponse> {
    return this.apiClient.call<{}, ProductsApiResponse>({
      apiType: ApiType.getProductsByMerchantId,
      requestType: RequestType.GET,
      pathVariables: [merchantId],
      body: {page: pageNumber}
    })
  }

  registerMerchant(registerMerchantRequest: RegisterMerchantRequest): Observable<void> {
    return this.apiClient.call<RegisterMerchantRequest, void>({
      apiType: ApiType.registerMerchant,
      requestType: RequestType.POST,
      body: registerMerchantRequest
    })
  }

  merchantLogin(email: string, password: string): Observable<JWTToken> {
    return this.apiClient.call<{}, JWTToken>({
      apiType: ApiType.merchantLogin,
      requestType: RequestType.POST,
      body: {
        email: email,
        password: password
      }
    }).pipe(
      map((response: JWTToken) => {
        console.log("Saving merchant JWT after merchantLogin", response)
        this.apiClient.saveJWTToken(response)
        this.authService.authChange
        return response
      })
    )
  }

  merchantForgetPassword(email: string,): Observable<void> {
    return this.apiClient.call<{}, void>({
      apiType: ApiType.merchantForgetPassword,
      requestType: RequestType.POST,
      body: {
        email: email,
      }
    }).pipe(
    )
  }


  merchantVerifyForgetPassword(token: string,): Observable<void> {
    let listDataUrl = [token];
    return this.apiClient.call<{}, void>({
      apiType: ApiType.merchantVerifyForgetPassword,
      requestType: RequestType.GET,
      listDataUrl: listDataUrl
    }).pipe(
    )
  }

  merchantUpdateForgetPassword(token: string, newPassword: string): Observable<void> {
    let dataUrl = token;
    let parameters = {
      password: newPassword,
    }
    return this.apiClient.call<{}, void>({
      apiType: ApiType.merchantVerifyForgetPassword,
      requestType: RequestType.POST,
      dataUrl: dataUrl,
      body: parameters,
    }).pipe(
    )
  }


  merchantChangePassword(newPassword: string,): Observable<void> {
    return this.apiClient.call<{}, void>({
      apiType: ApiType.merchantChangePassword,
      requestType: RequestType.POST,
      body: {
        newPassword: newPassword,
      }
    }).pipe(
    )
  }


  addMerchantPhone(
    countryCode: string,
    phoneNumber: string,
    hasWhatsapp: boolean,
    hasTelegram: boolean
  ): Observable<void> {
    return this.apiClient.call<{}, void>({
      apiType: ApiType.addMerchantPhone,
      requestType: RequestType.POST,
      pathVariables: [this.apiClient.getJwt()?.user?.merchantId ?? ""],
      body: {
        "countryCode": countryCode,
        "phoneNumber": phoneNumber,
        "hasWhatsapp": hasWhatsapp,
        "hasTelegram": hasTelegram
      }
    })
  }

  resendMerchantPhoneSMS(countryCode: string, phoneNumber: string): Observable<void> {
    return this.apiClient.call<{}, void>({
      apiType: ApiType.resendMerchantVerificationSMSCode,
      requestType: RequestType.POST,
      pathVariables: [this.apiClient.getJwt()?.user?.merchantId ?? ""],
      body: {
        "countryCode": countryCode,
        "phoneNumber": phoneNumber
      }
    })
  }

  getMerchantFilters(): Observable<MerchantFilters> {
    return this.apiClient.call<null, MerchantFilters>({
      apiType: ApiType.getMerchantFilters,
      requestType: RequestType.GET
    })
  }

  claimMerchant(merchantId: string, claimMerchantRequest: ClaimMerchantRequest): Observable<void> {
    return this.apiClient.call<ClaimMerchantRequest, void>({
      apiType: ApiType.claimMerchant,
      requestType: RequestType.POST,
      pathVariables: [merchantId],
      body: claimMerchantRequest
    })
  }

  updateMerchantInfo(
    profilePictureUrl?: string,
    coverPhotoUrl?: string,
    brief?: string,
    taxIdentificationNumber?: string,
    images?: string[],
    phones?: MerchantPhone[],
    websites?: string[],
    socialMediaPlatforms?: SocialMedia[]
  ): Observable<void> {
    let request: any = {}
    if (profilePictureUrl) {
      request["profilePictureURL"] = profilePictureUrl
    }
    if (coverPhotoUrl) {
      request["coverPhotoURL"] = coverPhotoUrl
    }
    if (brief) {
      request["brief"] = brief
    }
    if (taxIdentificationNumber) {
      request["taxIdentificationNumber"] = taxIdentificationNumber
    }
    if (images) {
      request["images"] = Array.from(new Set(images)); // Remove duplicates.
      // request["images"] = images
    }
    if (phones) {
      request["phones"] = phones
    }
    if (websites) {
      request["websites"] = websites
    }
    if (socialMediaPlatforms) {
      request["socialMediaPlatforms"] = socialMediaPlatforms
    }

    console.log("Updating merchant info with JWT", this.apiClient.getJwt())
    return this.apiClient.call<{}, void>({
      apiType: ApiType.merchantUpdateAccount,
      pathVariables: [this.apiClient.getJwt()?.user.merchantId ?? ''],
      requestType: RequestType.POST,
      body: request
    })
  }

  updateMerchantContactInfo(websites?: string[], socialMediaPlatforms?: SocialMedia[]): Observable<void> {
    let request: any = {}
    if (websites) {
      request["websites"] = websites
    }
    if (socialMediaPlatforms) {
      request["socialMediaPlatforms"] = socialMediaPlatforms
    }
    return this.apiClient.call<{}, void>({
      apiType: ApiType.merchantUpdateAccount,
      pathVariables: [this.apiClient.getJwt()?.user.merchantId ?? ''],
      requestType: RequestType.POST
    })
  }

  getMerchantRatings(merchantId: string): Observable<RatingApiResponse> {
    return this.apiClient.call<{}, RatingApiResponse>({
      apiType: ApiType.getMerchantRatingById,
      pathVariables: [merchantId],
      requestType: RequestType.GET
    })
  }

  writeReview(id: string, rate: number, comment: string): Observable<void> {
    let body: { [key: string]: any } = {
      rating: rate
    }
    if (comment.length > 0) {
      body['comment'] = comment
    }
    return this.apiClient.call<{}, void>({
      apiType: ApiType.addMerchantRating,
      requestType: RequestType.POST,
      body: body,
      pathVariables: [id]
    })
  }
}
